import { useRef, type CSSProperties, type ReactNode } from "react";

const BLUR_IN_STAGGER_MS = 40;

/**
 * Text that blurs in a word at a time as it streams, the way an assistant
 * message does.
 *
 * A message gets this from Streamdown's animate plugin, which only reads
 * markdown; a plain string that streams (a tool's input, a web profile being
 * written) gets its spans built here by hand, against keyframes of our own
 * (see `styles/animations.css`).
 */
export default function BlurInText({
  text,
  isWriting,
}: {
  text: string;
  /** Off once the text is settled, so re-reading it stays still. */
  isWriting: boolean;
}): ReactNode {
  const delaysRef = useRef<number[]>([]);
  if (!isWriting) return text;

  // Whitespace is a token of its own: that keeps a word's index stable as the
  // text grows, and an index that moved would remount its span and animate an
  // already-read word a second time.
  const tokens = text.split(/(\s+)/);
  const delays = delaysRef.current;

  // Text ending on a space ends on an empty token: the word still to come,
  // whose delay is set once it is written.
  const written =
    tokens[tokens.length - 1] === "" ? tokens.length - 1 : tokens.length;
  // Text that shrank keeps the delays of the tokens still there: recomputing
  // them would move a delay and restart a word already read.
  if (written < delays.length) delays.length = written;
  // Assigned on a token's first render and never recomputed. The new words of
  // a render follow each other by one stagger, as Streamdown's do; whitespace
  // takes no turn.
  let newWords = 0;
  for (let index = delays.length; index < written; index += 1) {
    delays[index] = newWords * BLUR_IN_STAGGER_MS;
    if (tokens[index].trim() !== "") newWords += 1;
  }

  return tokens.map((token, index) =>
    token.trim() === "" ? (
      token
    ) : (
      <span
        key={index}
        className="blur-in-word"
        style={{ "--blur-in-word-delay": `${delays[index]}ms` } as CSSProperties}
      >
        {token}
      </span>
    )
  );
}
